import { OpenInNew } from "@mui/icons-material";
import { Box, IconButton, Snackbar } from "@mui/material";
import type { ObjectId } from "@novorender/api";
import type { ComponentType, Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MemoryRouter, Route } from "react-router-dom";
import ReactVirtualizedAutoSizer from "react-virtualized-auto-sizer";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { LinearProgress } from "components";
import type { ObjectTreeRef } from "components/objectTree/objectTree";
import { ObjectTree } from "components/objectTree/objectTree";
import type { BreadcrumbNodeSpec, ListNode, Node, Tree } from "components/objectTree/types";
import { defaultBreadcrumbNodeLabel, expandGroup, makeNodeList } from "components/objectTree/utils";
import SimpleSnackbar from "components/simpleSnackbar";
import { featuresConfig } from "config/features";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { readyPdfObjectIds } from "contexts/explorerGlobals/reducer";
import { useHidden } from "contexts/hidden";
import { useHighlighted } from "contexts/highlighted";
import { useSelectionBasket } from "contexts/selectionBasket";
import { useShown } from "contexts/shown";
import { groupsActions } from "features/groups";
import { selectDefaultVisibility, selectMainObject, selectMainObjects } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useLoadAncestorsForSelection } from "hooks/useLoadAncestorsForSelection";
import { useOpenWidget } from "hooks/useOpenWidget";
import { usePreloadDescendants } from "hooks/usePreloadDescendants";
import { usePreloadOnExpand } from "hooks/usePreloadOnExpand";
import { selectIsProcoreDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { getFilePathFromObjectPath, getParentPaths, isContainerFileNode } from "utils/objectData";
import type { SelectionSet } from "utils/selectionSet";

import { useFileTypeLoadGuard } from "../hooks/useFileTypeLoadGuard";
import { useFileTypeObjects } from "../hooks/useFileTypeObjects";
import { SaveToGroups } from "../routes/saveToGroups";
import type { ModelTreeFilter, ModelTreeNodeData, TreeNodeRendererProps } from "../types";
import { collapseNode, expandNode, getObjectParentFile, loadMore, loadUpTo, selectNode } from "../utils";
import { getModelTreeVisibilityMode, getRevealedIdSet } from "../visibility";
import { ModelTreeNode } from "./modelTreeNode";

const EMPTY_ID_ARR: ObjectId[] = [];
const EMPTY_ID_SETS: SelectionSet[] = [];
// How many rows past the last visible row to look ahead when deciding whether to
// lazily page in more of a folder's children, so the next page arrives slightly
// before the user reaches the very bottom of what's loaded.
const PREFETCH_ROWS = 8;

export const ModelTree = React.memo(function ModelTree({
    header,
    fileLevel = false,
    fileTypes = [],
    tree,
    setTree: setTreeState,
    autoHeight,
    isInsideWidget,
    NodeRenderer = ModelTreeNode,
    itemSize = 28,
    filteredObjectIds,
    searchTerm,
    onNodeRightClick,
    isFilterActive = false,
    noHorizontalScroll,
    headerMode = "breadcrumbs",
    scrollAlignment,
    grouping = true,
}: {
    header?: React.ReactNode;
    fileLevel?: boolean;
    fileTypes?: ModelTreeFilter[];
    tree: Tree<ModelTreeNodeData>;
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
    autoHeight?: boolean;
    isInsideWidget?: boolean;
    NodeRenderer?: ComponentType<TreeNodeRendererProps>;
    itemSize?: number;
    filteredObjectIds?: Set<number> | null;
    searchTerm?: string;
    onNodeRightClick?: (x: number, y: number, item: ListNode<ModelTreeNodeData>) => void;
    /** When true, the tree is driven by search results — selection-driven tree mutations are skipped. */
    isFilterActive?: boolean;
    /** Disables horizontal overflow — items fill viewport width and names truncate with ellipsis. */
    noHorizontalScroll?: boolean;
    /** Controls the contextual header — see ObjectTree's headerMode prop for options. Defaults to "breadcrumbs". */
    headerMode?: "breadcrumbs" | "sticky" | "none";
    /** Controls scroll alignment when scrolling to a selected item. Passed through to ObjectTree. */
    scrollAlignment?: "top" | "center";
    /** When false, same-named siblings are never collapsed into a group pill — each node gets its own row. */
    grouping?: boolean;
}) {
    const {
        state: { dataAccess, pdfObjectDiscovery },
    } = useExplorerGlobals(true);
    const objectTreeRef = useRef<ObjectTreeRef | null>(null);
    const { t } = useTranslation();
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const skipPdfFiles = isProcoreDesign;
    // A hit spares a sheet its metadata load; everything else still resolves the long way.
    const knownPdfObjectIds = isProcoreDesign ? readyPdfObjectIds(pdfObjectDiscovery) : undefined;
    const mainObjectRef = useRef(mainObject);
    mainObjectRef.current = mainObject;
    const [hasBackgroundChildrenLoaders, setHasBackgroundChildrenLoaders] = useState(false);
    const backgroundChildrenLoadersRef = useRef(
        new Map<string, { loadUpToPath: string; abortController: AbortController }>(),
    );
    // Background loaders that fully drain a folder's remaining children (replacing the old
    // "More..." button). Keyed by folder path so a folder is only drained once at a time.
    const autoLoadControllersRef = useRef(new Map<string, AbortController>());

    const refreshBackgroundLoadersFlag = useCallback(() => {
        setHasBackgroundChildrenLoaders(
            backgroundChildrenLoadersRef.current.size > 0 || autoLoadControllersRef.current.size > 0,
        );
    }, []);

    const [loading, setLoading] = useState(false);
    const [createdGroupId, setCreatedGroupId] = useState<string | undefined>();
    const [height, setHeight] = useState(0);
    const [filterAbortController, abortFilter] = useAbortController();
    const openWidget = useOpenWidget();
    const dispatch = useAppDispatch();
    const { fileTypeObjects, loadFileTypeObjects } = useFileTypeObjects();

    // Path of the object to highlight/scroll to. Resolved directly from the
    // selected object (which carries its path) rather than by scanning the tree
    // for a node whose id matches — the clicked object (e.g. a fragment/leaf) is
    // not always stored as a findable tree node, so the id scan would miss it.
    const [selectedPath, setSelectedPath] = useState<string | undefined>(undefined);
    // Bumped on every canvas-originated selection so the tree re-scrolls even
    // when the new selection resolves to the same path string as the previous
    // one (common in models with duplicate sibling paths) after the user has
    // scrolled away.
    const [scrollToVersion, setScrollToVersion] = useState(0);
    const [error, setError] = useState("");

    // Tracks a main-object selection that originated from a click inside the tree itself.
    // Selecting a row in the tree should NOT scroll it to the top — that auto-scroll is
    // reserved for selections coming from the 3D view. The ref holds the id the tree just
    // selected so the selection effect can skip the scroll for it.
    const treeSelectedMainObjectRef = useRef<number | undefined>(undefined);
    const handleSelectInTree = useCallback((mainObjectId: number | undefined) => {
        treeSelectedMainObjectRef.current = mainObjectId;
    }, []);

    const treeRef = useRef(tree);
    treeRef.current = tree;

    // Keep treeRef.current authoritative synchronously: setTreeState only updates
    // the rendered state on the next render, so back-to-back tree mutations within
    // a single tick (e.g. apply() followed by background loadUpTo calls) would
    // otherwise read a stale tree. Routing every write through this wrapper updates
    // the ref eagerly so subsequent reads see the latest tree.
    const setTree = useCallback<Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>>(
        (update) => {
            const next = typeof update === "function" ? update(treeRef.current) : update;
            treeRef.current = next;
            setTreeState(next);
        },
        [setTreeState],
    );

    // Preload descendant IDs for unexpanded folders so state propagates upward.
    // Selection and visibility checkboxes both rely on the per-node descendants arrays.
    const highlightedSet = useHighlighted();
    const hiddenSet = useHidden();
    const shownSet = useShown();
    const basket = useSelectionBasket();
    const visibilityMode = getModelTreeVisibilityMode(
        useAppSelector(selectDefaultVisibility),
        useAppSelector(selectIsProcoreDesign),
    );
    const revealedSet = useMemo(
        () => (visibilityMode === "additive" ? getRevealedIdSet(shownSet.ids, basket.ids) : undefined),
        [visibilityMode, shownSet, basket],
    );
    const preloadIdSets = useMemo(() => {
        if (isFilterActive) {
            return EMPTY_ID_SETS;
        }

        const sets = [highlightedSet.ids, hiddenSet.ids, revealedSet].filter(
            (ids) => ids !== undefined && ids.size > 0,
        ) as SelectionSet[];

        return sets.length ? sets : EMPTY_ID_SETS;
    }, [isFilterActive, highlightedSet, hiddenSet, revealedSet]);
    const activeFileTypes = fileTypeObjects.status === AsyncStatus.Success ? fileTypeObjects.data : undefined;
    usePreloadDescendants(tree, dataAccess, preloadIdSets, setTree);

    // Preload descendants for child nodes when a folder is expanded, so that
    // subsequent checkbox clicks on those children resolve instantly.
    const preloadOnExpand = usePreloadOnExpand(dataAccess, tree, setTree);

    // Load the ancestor chain for all semantically selected objects so the tree can
    // expand to show them (box select, Select Similar, single click).
    // useLoadAncestorsForSelection already caps the sample at 200 IDs internally.
    const { isLoading: isAncestorLoading } = useLoadAncestorsForSelection(
        tree,
        dataAccess,
        isFilterActive || mainObjects.length === 0 ? EMPTY_ID_ARR : mainObjects,
        setTree,
        activeFileTypes,
        skipPdfFiles,
        knownPdfObjectIds,
    );

    useEffect(() => {
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            for (const { abortController } of backgroundChildrenLoadersRef.current.values()) {
                abortController.abort();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
            for (const abortController of autoLoadControllersRef.current.values()) {
                abortController.abort();
            }
        };
    }, []);

    const highlightPath = useMemo(() => {
        // Don't auto-scroll when search is active — the tree only shows search results.
        if (isFilterActive) {
            return undefined;
        }
        return selectedPath;
    }, [selectedPath, isFilterActive]);

    const handleTreeLoaderError = useCallback(
        (
            ex: unknown,
            rollback: undefined | ((tree: Tree<ModelTreeNodeData>) => Tree<ModelTreeNodeData>),
            resetLoading = false,
        ) => {
            if ((ex as { name: string }).name === "AbortError") {
                return;
            }
            console.warn(ex);
            telemetry.trackCaughtException({
                source: "modelTree.modelTree.handleTreeLoaderError",
                category: "data",
                error: ex,
            });
            setError("errorOccurred");
            if (rollback) {
                setTree(rollback);
            }
            if (resetLoading) {
                setLoading(false);
            }
        },
        [setTree],
    );

    useEffect(() => {
        abortFilter();
    }, [abortFilter, fileTypes]);

    useFileTypeLoadGuard({
        loadFileTypeObjects,
        fileTypes,
        setTree,
        filterAbortController,
        isFilterActive,
    });

    const tryExpandNode = useCallback(
        async (tree: Tree<ModelTreeNodeData>, path: string) => {
            const expander = expandNode({
                tree,
                path,
                dataAccess,
                abortSignal: filterAbortController.current.signal,
                fileTypes: activeFileTypes,
                skipPdfFiles,
                knownPdfObjectIds,
            });
            setTree(expander.tree);
            if (!expander.load) {
                // Node was already loaded — preload its children's descendants
                preloadOnExpand(path);
                return;
            }

            try {
                const patch = await expander.load();
                setTree((tree) => patch(tree, true));
                // Preload descendants for newly visible children
                preloadOnExpand(path);
            } catch (ex) {
                handleTreeLoaderError(ex, expander.rollback);
            }
        },
        [
            dataAccess,
            filterAbortController,
            activeFileTypes,
            handleTreeLoaderError,
            setTree,
            skipPdfFiles,
            knownPdfObjectIds,
            preloadOnExpand,
        ],
    );

    useEffect(() => {
        // When a search is active, the tree layout is driven entirely by
        // search results — ignore mainObject changes from 3D scene clicks.
        if (isFilterActive) {
            return;
        }

        // Cancel the previous selection run on every new selection. Each run starts
        // metadata resolution, selectNode (critical-path + child loads), and background
        // loadUpTo fills; without cancellation, rapid clicking (common on large models
        // like Mastodon) leaves many concurrent loads alive, retaining references and
        // growing render-process memory until the tab crashes (Error Code 5). The run
        // signal is chained to the long-lived filter signal so a filter change still
        // tears everything down.
        const runAbortController = new AbortController();
        const filterSignal = filterAbortController.current.signal;
        const propagateFilterAbort = () => runAbortController.abort();
        if (filterSignal.aborted) {
            runAbortController.abort();
        } else {
            filterSignal.addEventListener("abort", propagateFilterAbort);
        }

        expandOrSelectNode();

        async function expandOrSelectNode() {
            if (fileTypeObjects.status !== AsyncStatus.Success) {
                return;
            }
            const abortSignal = runAbortController.signal;

            let obj =
                typeof mainObject === "number" &&
                (Object.values(treeRef.current).find((node) => node.data.object.id === mainObject)?.data.object ??
                    (await dataAccess.getObject(mainObject, abortSignal)));

            if (abortSignal.aborted) {
                return;
            }

            if (obj) {
                if (fileLevel) {
                    const parent = await getObjectParentFile({
                        dataAccess,
                        mainObject: obj,
                        fileTypes: fileTypeObjects.data.length ? fileTypeObjects.data : undefined,
                        tree: treeRef.current,
                        abortSignal: abortSignal,
                    });
                    if (parent) {
                        obj = parent;
                    }
                }
                // Skip the auto-scroll when this selection was initiated from within the tree
                // (clicking a row). highlightPath drives the scroll, so leaving it unchanged
                // keeps the clicked row in place. 3D-view selections still scroll to the row.
                const initiatedInTree = treeSelectedMainObjectRef.current === mainObject;
                treeSelectedMainObjectRef.current = undefined;
                if (!initiatedInTree) {
                    setSelectedPath(obj.path);
                    setScrollToVersion((v) => v + 1);
                }
                if (abortSignal.aborted) {
                    return;
                }

                // A selection that originated from clicking a row in the tree must not
                // restructure the tree. In particular, selecting a grouped ("clickable
                // count") row should not un-collapse the clicked instance and pop it out
                // of its group. The row is already visible, so there is nothing to expand.
                if (initiatedInTree && treeRef.current[obj.path]) {
                    return;
                }

                const fileTypes = fileTypeObjects.data.length ? fileTypeObjects.data : undefined;
                const loader = selectNode({
                    tree: treeRef.current,
                    dataAccess,
                    abortSignal: abortSignal,
                    mainObject: obj,
                    fileTypes,
                    skipPdfFiles,
                    knownPdfObjectIds,
                });

                if (loader.tree === treeRef.current && !treeRef.current[""].children) {
                    tryExpandNode(treeRef.current, "");
                } else {
                    setTree(loader.tree);
                    if (loader.load) {
                        setLoading(true);
                        try {
                            const loadResult = await loader.load();
                            // A newer selection may have superseded this run while the
                            // load was in flight. Reject the stale result before mutating
                            // the tree so superseded loads don't pile up structural work.
                            if (abortSignal.aborted) {
                                setLoading(false);
                                return;
                            }
                            const relevant = mainObject === mainObjectRef.current;
                            setTree((tree) => loadResult.apply(tree, relevant));

                            // If there are unloaded children between the loaded ones and selected object -
                            // load it in the background.
                            // Loading them might feel pointless, but otherwise it might feel like missing data,
                            // because there might be gaps in the middle of the node children without any indication.
                            for (const { parentPath, loadUpToPath } of loadResult.loadUpTo || []) {
                                const currentLoader = backgroundChildrenLoadersRef.current.get(parentPath);
                                if (currentLoader) {
                                    if (loadUpToPath <= currentLoader.loadUpToPath) {
                                        continue;
                                    }
                                    currentLoader.abortController.abort();
                                }

                                const abortController = new AbortController();
                                const abort = () => abortController.abort();
                                // Chain to the per-run signal (not the long-lived filter signal) so a
                                // newer selection also tears down this selection's background gap-fills.
                                runAbortController.signal.addEventListener("abort", abort);
                                backgroundChildrenLoadersRef.current.set(parentPath, { loadUpToPath, abortController });
                                setHasBackgroundChildrenLoaders(true);
                                loadUpTo({
                                    dataAccess,
                                    tree: treeRef.current,
                                    path: parentPath,
                                    loadUpToPath,
                                    fileTypes,
                                    abortSignal: abortController.signal,
                                    skipPdfFiles,
                                    knownPdfObjectIds,
                                })
                                    .then((patch) => {
                                        setTree(patch);
                                        setTimeout(() => {
                                            objectTreeRef.current?.rescroll();
                                        }, 100);
                                    })
                                    .catch((ex) => {
                                        if ((ex as { name: string }).name === "AbortError") {
                                            return;
                                        }
                                        console.error(ex);
                                    })
                                    .finally(() => {
                                        runAbortController.signal.removeEventListener("abort", abort);
                                        if (
                                            backgroundChildrenLoadersRef.current.get(parentPath)?.loadUpToPath ===
                                            loadUpToPath
                                        ) {
                                            backgroundChildrenLoadersRef.current.delete(parentPath);
                                            refreshBackgroundLoadersFlag();
                                        }
                                    });
                            }

                            setLoading(false);
                        } catch (ex) {
                            handleTreeLoaderError(ex, loader.rollback, true);
                        }
                    }
                }
            } else if (!treeRef.current[""].children) {
                tryExpandNode(treeRef.current, "");
            }
        }

        return () => {
            // Supersede this run: aborts in-flight metadata/selectNode/child loads and,
            // via the chained signal, this selection's background gap-fills.
            filterSignal.removeEventListener("abort", propagateFilterAbort);
            runAbortController.abort();
        };
    }, [
        dataAccess,
        mainObject,
        fileTypeObjects,
        fileLevel,
        filterAbortController,
        tryExpandNode,
        handleTreeLoaderError,
        setTree,
        isFilterActive,
        skipPdfFiles,
        knownPdfObjectIds,
        refreshBackgroundLoadersFlag,
    ]);

    // Load a single additional page of a folder's children. Lazy replacement for
    // the old "More..." button and the eager background drain: pages are fetched
    // on demand as the user scrolls toward the end of a folder's loaded children
    // (see loadMoreForVisibleRange), not by draining every expanded folder up
    // front. One loader per folder path via autoLoadControllersRef.
    const loadNextPage = useCallback(
        async (path: string) => {
            if (autoLoadControllersRef.current.has(path)) {
                return;
            }
            const node = treeRef.current[path];
            if (!node || !node.hasMoreChildren || !node.children || node.loading) {
                return;
            }

            const abortController = new AbortController();
            const onFilterAbort = () => abortController.abort();
            const currentFilterAbortController = filterAbortController.current;
            currentFilterAbortController.signal.addEventListener("abort", onFilterAbort);
            autoLoadControllersRef.current.set(path, abortController);
            refreshBackgroundLoadersFlag();

            try {
                const loader = loadMore({
                    dataAccess,
                    tree: treeRef.current,
                    path,
                    fileTypes: activeFileTypes,
                    abortSignal: abortController.signal,
                    skipPdfFiles,
                    knownPdfObjectIds,
                });
                if (!loader.load) {
                    return;
                }
                setTree(loader.tree);
                try {
                    const patch = await loader.load();
                    setTree((tree) => patch(tree, true));
                } catch (ex) {
                    handleTreeLoaderError(ex, loader.rollback);
                }
            } finally {
                currentFilterAbortController.signal.removeEventListener("abort", onFilterAbort);
                autoLoadControllersRef.current.delete(path);
                refreshBackgroundLoadersFlag();
            }
        },
        [
            dataAccess,
            activeFileTypes,
            filterAbortController,
            handleTreeLoaderError,
            setTree,
            skipPdfFiles,
            knownPdfObjectIds,
            refreshBackgroundLoadersFlag,
        ],
    );

    // Lazily page in more children as the user scrolls. When the bottom of the
    // viewport (plus a small look-ahead) reaches the end of a folder's loaded
    // children and that folder still has more, fetch its next page. Because a new
    // page grows the list and re-fires this callback, a folder whose boundary
    // stays on screen keeps loading until the viewport is filled — then it waits
    // for the next scroll.
    const loadMoreForVisibleRange = useCallback(
        (_visibleStartIndex: number, visibleStopIndex: number) => {
            if (isFilterActive) {
                return;
            }
            const currentList = listRef.current;
            const currentTree = treeRef.current;
            if (currentList.length === 0) {
                return;
            }

            const maxRelevant = Math.min(currentList.length - 1, visibleStopIndex + PREFETCH_ROWS);
            const probeItem = currentList[maxRelevant];
            if (!probeItem) {
                return;
            }

            // Nearest ancestor folder first (the probe node itself, then up to root),
            // so we page the folder the user is actually scrolling through.
            const probePath = probeItem.node.path;
            const candidates = [probePath, ...[...getParentPaths(probePath, true)].reverse()];
            const seen = new Set<string>();
            for (const folderPath of candidates) {
                if (seen.has(folderPath)) {
                    continue;
                }
                seen.add(folderPath);

                const folder = currentTree[folderPath];
                if (!folder?.hasMoreChildren || folder.loading) {
                    continue;
                }
                if (autoLoadControllersRef.current.has(folderPath)) {
                    return;
                }

                // If the row just past the look-ahead window still belongs to this
                // folder, its loaded children extend beyond the window — the user is
                // mid-folder, so don't page yet. Otherwise the folder's loaded region
                // ends within the window (its boundary is on/near screen) → page it.
                const prefix = folderPath === "" ? "" : `${folderPath}/`;
                const nextIndex = maxRelevant + 1;
                const extendsPastWindow =
                    nextIndex < currentList.length && currentList[nextIndex].node.path.startsWith(prefix);
                if (!extendsPastWindow) {
                    void loadNextPage(folderPath);
                    return;
                }
            }
        },
        [isFilterActive, loadNextPage],
    );

    const handleToggleExpand = useCallback(
        async ({ node, nodes }: ListNode<ModelTreeNodeData>, specificNode?: Node<ModelTreeNodeData>) => {
            if (specificNode) {
                if (specificNode.expanded) {
                    setTree(collapseNode({ tree, path: specificNode.path }));
                } else {
                    tryExpandNode(tree, specificNode.path);
                }
                return;
            }

            const firstNode = node;
            const lastNode = nodes?.at(-1) ?? node;
            const allExpanded = (nodes?.every((n) => n.expanded) ?? true) && node.expanded;
            if (allExpanded) {
                setTree(collapseNode({ tree, path: firstNode.path }));
            } else {
                tryExpandNode(tree, lastNode.path);
            }
        },
        [tree, tryExpandNode, setTree],
    );

    const handleExpandGroup = useCallback(
        ({ node }: ListNode<ModelTreeNodeData>) => {
            setTree((tree) => expandGroup(tree, node.path, 100));
        },
        [setTree],
    );

    const handleGroupCreated = useCallback((groupId: string) => {
        setCreatedGroupId(groupId);
    }, []);

    const handleOpenGroup = useCallback(() => {
        openWidget(featuresConfig.groups.key, { force: true });
        if (createdGroupId) {
            dispatch(groupsActions.setHighlightGroupInWidget(createdGroupId));
            setCreatedGroupId(undefined);
        }
    }, [dispatch, createdGroupId, openWidget]);

    const isStickyHeaderCandidate = useCallback(
        (node: Node<ModelTreeNodeData>) => node.expanded && isContainerFileNode(node.path),
        [],
    );

    // This is the rendered-row flattener. Callers that maintain their own interaction
    // list (ObjectTreeWindowV2 does for range selection) must pass the same grouping mode.
    const list = useMemo(() => makeNodeList({ tree, grouping }), [tree, grouping]);
    // Keep the latest list available to the scroll-driven lazy loader without
    // re-subscribing the callback on every list change.
    const listRef = useRef(list);
    listRef.current = list;

    useEffect(() => {
        if (autoHeight) {
            setHeight((height) => Math.max(height, list.length * itemSize));
        }
    }, [list, autoHeight, itemSize]);

    return (
        <MemoryRouter>
            <Route path="/saveToGroups">
                <SaveToGroups onCreate={handleGroupCreated} headerShadow={isInsideWidget} />
            </Route>
            <Route>
                {({ match }) => (
                    <Box
                        sx={{
                            position: "relative",
                            minHeight: `${itemSize}px`,
                            height: autoHeight ? `${height}px` : "100%",
                            display: match?.isExact ? "flex" : "none",
                            flexDirection: "column",
                        }}
                    >
                        {header}
                        <Box
                            sx={{
                                position: "relative",
                                flex: "auto",
                                overflow: noHorizontalScroll ? "hidden" : undefined,
                            }}
                        >
                            <ReactVirtualizedAutoSizer>
                                {({ height, width }: { height: number; width: number }) => (
                                    <ObjectTree
                                        ref={objectTreeRef}
                                        width={width}
                                        height={height}
                                        itemSize={itemSize}
                                        tree={tree}
                                        list={list}
                                        scrollTo={highlightPath}
                                        scrollToVersion={scrollToVersion}
                                        onVisibleRangeChange={loadMoreForVisibleRange}
                                        noHorizontalScroll={noHorizontalScroll}
                                        headerMode={headerMode}
                                        scrollAlignment={scrollAlignment}
                                        isStickyHeaderCandidate={isStickyHeaderCandidate}
                                        ListItem={(props) => (
                                            <NodeRenderer
                                                {...props}
                                                setLoading={setLoading}
                                                onToggleExpanded={handleToggleExpand}
                                                onExpandGroup={handleExpandGroup}
                                                onSelectInTree={handleSelectInTree}
                                                abortSignal={filterAbortController.current.signal}
                                                onNodeRightClick={onNodeRightClick}
                                                highlightPath={highlightPath}
                                                tree={tree}
                                                filteredObjectIds={filteredObjectIds}
                                                searchTerm={searchTerm}
                                            />
                                        )}
                                        buildBreadcrumbs={buildBreadcrumbs}
                                    />
                                )}
                            </ReactVirtualizedAutoSizer>
                        </Box>
                        {(tree[""].loading || loading || hasBackgroundChildrenLoaders || isAncestorLoading) && (
                            <LinearProgress />
                        )}
                    </Box>
                )}
            </Route>
            <SimpleSnackbar hideDuration={3000} close={() => setError("")} message={error ? t(error) : ""} />
            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{
                    width: { xs: "auto", sm: 350 },
                    bottom: { xs: "auto", sm: 24 },
                    top: { xs: 24, sm: "auto" },
                }}
                autoHideDuration={3500}
                open={Boolean(createdGroupId)}
                onClose={() => setCreatedGroupId(undefined)}
                message={t("groupCreatedSaveInGroups")}
                action={
                    <IconButton size="small" color="inherit" onClick={handleOpenGroup}>
                        <OpenInNew fontSize="small" />
                    </IconButton>
                }
            />
        </MemoryRouter>
    );
});

// If there's file in the path - collapse things before and after.
// Otherwise collapse everything but the last 3 nodes.
function buildBreadcrumbs(nodes: Node<ModelTreeNodeData>[]) {
    const fileIndex = nodes.findIndex((node) => getFilePathFromObjectPath(node.path));
    if (fileIndex === -1) {
        return nodes.length > 3
            ? [
                  nodes.slice(0, -2),
                  ...nodes.slice(-2).map((node) => ({ node, label: defaultBreadcrumbNodeLabel(node) })),
              ]
            : nodes.map((node) => ({ node, label: defaultBreadcrumbNodeLabel(node) }));
    }

    const result: BreadcrumbNodeSpec<ModelTreeNodeData>[] = [];
    if (fileIndex > 1) {
        result.push(nodes.slice(0, fileIndex));
    } else {
        for (let i = 0; i < fileIndex; i++) {
            result.push({ node: nodes[i], label: defaultBreadcrumbNodeLabel(nodes[i]) });
        }
    }

    result.push({ node: nodes[fileIndex], label: defaultBreadcrumbNodeLabel(nodes[fileIndex]) });

    if (nodes.length - fileIndex > 2) {
        result.push(nodes.slice(fileIndex + 1, nodes.length - 1));
        const lastNode = nodes.at(-1)!;
        result.push({ node: lastNode, label: defaultBreadcrumbNodeLabel(lastNode) });
    } else {
        for (let i = fileIndex + 1; i < nodes.length; i++) {
            result.push({ node: nodes[i], label: defaultBreadcrumbNodeLabel(nodes[i]) });
        }
    }

    return result;
}
